import { createSlice } from '@reduxjs/toolkit';
import {
  fetchCandidateById,
  fetchSimilarContacts,
  addContactsTags,
  removeContactsTags,
  fetchContactsActivities,
  fetchContactsOutreaches,
  fetchContactsConversations,
  fetchScheduledConversations,
} from './actions';

const initialState = {
  candidate: {
    error: false,
    isLoading: true,
    data: {},
  },

  activities: {
    isLoading: true,
    data: [],
    pagination: {
      page: 0,
      item: 50,
    },
    meta: {},
  },

  outreaches: {
    isLoading: true,
    data: [],
  },

  similarContacts: {
    id: null,
    pagination: {
      page: 1,
      item: 20,
    },
    isLoading: true,
    data: [],
  },

  conversations: {
    data: [],
    isLoading: true,
  },

  scheduledConversations: {
    data: [],
    isLoading: true,
  },

  pagination: {
    page: 0,
    items: 50,
  },

  filters: {
    changed: false,

    user: [],
    company: [],
    location: [],
    industry: [],
    employeeRange: [],
    jobTitle: [],
    tags: [],
    skills: [],
    topTalent: false,
    outreach: [],
    outreachStatus: null,
    sourcedBy: [],
    contactStage: [],
    pastCompany: [],
  },

  search: {
    term: null,
  },

  sort: {
    element: 'tst',
    order: 'desc',
  },
};

const { reducer, actions } = createSlice({
  name: 'contacts',
  initialState,

  reducers: {
    applyFilter: (state, { payload }) => {
      const { type, value, selected } = payload;
      if (value?.length || value) {
        state.filters[type] = value;
      } else {
        state.filters[type] = selected;
      }
      state.filters.changed = true;
      state.page = { ...initialState.page };
      if (type === 'company') state.filters.pastCompany = [];
      if (type === 'pastCompany') state.filters.company = [];
    },

    clearFilter: (state, { payload }) => {
      const { type, isMulti = false } = payload;
      state.filters[type] = isMulti ? [] : null;
    },

    resetFilter: (state) => {
      state.filters = { ...initialState?.filters, changed: false };
      state.page = { ...initialState.page };
    },

    applySort: (state, { payload }) => {
      const { element, order } = payload;
      state.sort.element = element;
      state.sort.order = order;
    },

    applyPage: (state, { payload }) => {
      const { page } = payload;
      state.pagination.page = page;
    },

    applyActivitiesPage: (state, { payload }) => {
      const { page } = payload;
      state.activities.pagination.page = page;
    },

    applySearchTerm: (state, { payload }) => {
      const { searchTerm } = payload;
      state.page = { ...initialState.page };
      state.search.term = searchTerm;
    },

    applyAllFilter: (state, action) => {
      state.page = { ...initialState.page };
      state.filters.changed = true;
      state.filters['company'] = action.payload.company;
      state.filters['location'] = action.payload.location;
      state.filters['industry'] = action.payload.industry;
      state.filters['employeeRange'] = action.payload.employeeRange;
      state.filters['jobTitle'] = action.payload.jobTitle;
      state.filters['tags'] = action.payload.tags;
      state.filters['skills'] = action.payload.skills;
      state.filters['outreachStatus'] = action.payload.outreachStatus;
    },

    clearAllFilter: (state) => {
      state.page = { ...initialState.page };
      state.filters = {
        ...initialState.filters,
        user: [null],
        changed: false,
      };
    },
    updateCandidateDetails: (state, { payload }) => {
      const newCandidateData = { ...state.candidate.data, ...payload };
      state.candidate.data = newCandidateData;
    },
  },

  extraReducers: {
    [fetchCandidateById.pending]: (state, { meta }) => {
      const {
        arg: { background = false },
      } = meta;
      if (background) return;
      state.candidate.error = false;
      state.candidate.isLoading = true;
    },
    [fetchCandidateById.fulfilled]: (state, { meta, payload }) => {
      const {
        arg: { background = false },
      } = meta;
      const contact = payload?.data?.contact || {};
      if (contact?.id) {
        state.candidate.data = contact;
      } else {
        state.candidate.error = true;
      }
      if (!background) {
        state.candidate.isLoading = false;
      }
    },

    [fetchSimilarContacts.pending]: (state) => {
      state.similarContacts.isLoading = true;
    },

    [fetchSimilarContacts.fulfilled]: (state, { meta, payload }) => {
      const {
        arg: { contactId },
      } = meta;

      if (state.similarContacts.id !== contactId) {
        state.similarContacts.id = contactId;
        state.similarContacts.data = payload?.data?.contact || [];
        state.similarContacts.isLoading = false;
        return;
      }

      if (state?.similarContacts?.data?.length) {
        const contacts = payload?.data?.contact || [];
        state.similarContacts.data = [
          ...state.similarContacts?.data,
          ...contacts,
        ];
      } else {
        state.similarContacts.data = payload?.data?.contact || [];
      }
      state.similarContacts.isLoading = false;
    },

    [addContactsTags.fulfilled]: (state, { payload }) => {
      const { data: tags } = payload;
      const candidateDetails = state?.candidate?.data;
      const currCandidate = { ...candidateDetails, ...tags };
      state.candidate.data = currCandidate;
    },

    [removeContactsTags.fulfilled]: (state, { payload }) => {
      const { data: tags = [] } = payload;
      const candidateDetails = state?.candidate?.data;
      const currCandidate = { ...candidateDetails, ...tags };
      state.candidate.data = currCandidate;
    },

    [fetchContactsActivities.pending]: (state) => {
      state.activities.isLoading = true;
    },

    [fetchContactsActivities.fulfilled]: (state, { payload }) => {
      state.activities.data = payload?.data || [];
      state.activities.meta = payload?.meta || {};
      state.activities.isLoading = false;
    },

    [fetchContactsOutreaches.pending]: (state) => {
      state.outreaches.isLoading = true;
    },

    [fetchContactsOutreaches.fulfilled]: (state, { payload }) => {
      state.outreaches.data = payload?.data || [];
      state.outreaches.isLoading = false;
    },

    [fetchContactsConversations.pending]: (state) => {
      state.conversations.isLoading = true;
    },

    [fetchContactsConversations.fulfilled]: (state, { payload }) => {
      state.conversations.data = payload?.data || [];
      state.conversations.isLoading = false;
    },

    [fetchScheduledConversations.pending]: (state) => {
      state.scheduledConversations.isLoading = true;
    },

    [fetchScheduledConversations.fulfilled]: (state, { payload }) => {
      state.scheduledConversations.data = payload?.data || [];
      state.scheduledConversations.isLoading = false;
    },
  },
});

export const {
  applyPage,
  applySearchTerm,
  applySort,
  applyFilter,
  clearFilter,
  resetFilter,
  applyAllFilter,
  clearAllFilter,
  updateCandidateDetails,
  applyActivitiesPage,
} = actions;

export default reducer;
